import { useState } from 'react';
import { profileApi } from '../lib/api';
import { getPrivateKey } from '../lib/crypto';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const INVITEE_TYPES = [
  { value: 'solicitor', label: 'Solicitor', group: 'legal' },
  { value: 'legal_advocate', label: 'Legal advocate', group: 'legal' },
  { value: 'support_worker', label: 'Support worker', group: 'support' },
  { value: 'counsellor', label: 'Counsellor', group: 'health' },
  { value: 'therapist', label: 'Therapist', group: 'health' },
  { value: 'gp_doctor', label: 'GP or doctor', group: 'health' },
  { value: 'family_member', label: 'Family member', group: 'personal' },
  { value: 'friend', label: 'Friend', group: 'personal' },
  { value: 'other', label: 'Other', group: 'other' },
];

const GROUP_OF = Object.fromEntries(INVITEE_TYPES.map((t) => [t.value, t.group]));

/**
 * Three-screen invitation flow, styled to match PasswordUnlockDialog.
 *
 * Screens:
 *   1. Explanation — "you are about to share access..."
 *   2. Details — email, type, permission (with tone-adaptive warning copy)
 *   3. Confirm — type invitee email back to confirm; signing happens here.
 */
export const InviteDialog = ({ open, onOpenChange, onCreated }) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [type, setType] = useState('solicitor');
  const [typeOther, setTypeOther] = useState('');
  const [permission, setPermission] = useState('read');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setStep(1);
    setEmail('');
    setType('solicitor');
    setTypeOther('');
    setPermission('read');
    setConfirmEmail('');
    setError('');
    setLoading(false);
  };

  const handleOpenChange = (next) => {
    if (loading) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const canProceed2 = (
    email.trim().includes('@') &&
    !!type &&
    (type !== 'other' || typeOther.trim().length > 1) &&
    !!permission
  );
  const canSubmit = canProceed2 && confirmEmail.trim().toLowerCase() === email.trim().toLowerCase();

  const handleSubmit = async () => {
    const privateKey = getPrivateKey();
    if (!privateKey) {
      setError('Your encryption key is not unlocked in this session. Sign out and back in, then try again.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await profileApi.createInvitation({
        invitee_email: email.trim().toLowerCase(),
        invitee_type: type,
        invitee_type_other: type === 'other' ? typeOther.trim() : null,
        permission,
        private_key_pem: privateKey,
      });
      toast.success(`Access granted. We have emailed ${email.trim().toLowerCase()}.`);
      onCreated?.(data);
      reset();
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Access could not be granted. Try again.';
      setError(detail);
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="invite-dialog" className="max-w-lg">
        {step === 1 && (
          <ExplanationStep onContinue={() => setStep(2)} onCancel={() => handleOpenChange(false)} />
        )}
        {step === 2 && (
          <DetailsStep
            email={email} setEmail={setEmail}
            type={type} setType={setType}
            typeOther={typeOther} setTypeOther={setTypeOther}
            permission={permission} setPermission={setPermission}
            canProceed={canProceed2}
            onBack={() => setStep(1)}
            onContinue={() => { setError(''); setStep(3); }}
          />
        )}
        {step === 3 && (
          <ConfirmStep
            email={email}
            confirmEmail={confirmEmail}
            setConfirmEmail={setConfirmEmail}
            canSubmit={canSubmit}
            loading={loading}
            error={error}
            onBack={() => { if (!loading) setStep(2); }}
            onSubmit={handleSubmit}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};


// =========================================================================
// Step 1 — Explanation
// =========================================================================
const ExplanationStep = ({ onContinue, onCancel }) => (
  <>
    <DialogHeader>
      <DialogTitle className="font-heading text-[22px] font-semibold tracking-[-0.02em]">
        You are about to share access to your records
      </DialogTitle>
      <DialogDescription className="text-ink-55">
        Read this before you continue.
      </DialogDescription>
    </DialogHeader>
    <div className="space-y-3">
      <div className="rounded-lg bg-ground p-4 space-y-3 text-[15px] leading-relaxed text-ink-70">
        <p>
          You are about to share your personal care records with another person.
          These records may contain sensitive information about your childhood,
          your family, your health and your time in care.
        </p>
        <p>
          Once shared, that person can read everything in your account. If you
          choose read and write, they can also act on it.
        </p>
      </div>
      <p className="px-3 py-2 rounded-md bg-amber-wash text-amber-ink text-sm">
        Only grant access to people you trust. You can revoke access at any time,
        but anything they have already seen or downloaded is outside our control.
      </p>
    </div>
    <DialogFooter>
      <Button type="button" variant="ghost" onClick={onCancel}
              data-testid="invite-explain-cancel">
        Cancel
      </Button>
      <Button type="button" onClick={onContinue}
              data-testid="invite-explain-continue">
        I understand, continue
      </Button>
    </DialogFooter>
  </>
);


// =========================================================================
// Step 2 — Details
// =========================================================================
const DetailsStep = ({
  email, setEmail, type, setType, typeOther, setTypeOther,
  permission, setPermission, canProceed, onBack, onContinue,
}) => {
  const group = GROUP_OF[type];
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-heading text-[22px] font-semibold tracking-[-0.02em]">
          Who are you inviting?
        </DialogTitle>
        <DialogDescription className="text-ink-55">
          Tell us who you are giving access to and what they can do.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="invitee-email" className="text-[15px] font-semibold">What is their email address?</Label>
          <p className="text-sm text-ink-55">
            They need a Precious Data account with this email, or they can create one.
          </p>
          <Input
            id="invitee-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
            autoComplete="off"
            data-testid="invite-email"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-[15px] font-semibold">What is their role?</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="invite-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INVITEE_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {type === 'other' && (
            <Input
              value={typeOther}
              onChange={(e) => setTypeOther(e.target.value)}
              placeholder="Describe their role"
              className="mt-2"
              data-testid="invite-type-other"
            />
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-[15px] font-semibold">What can they do?</Label>
          <RadioGroup value={permission} onValueChange={setPermission} className="space-y-2">
            <label
              className={`flex items-start gap-3 rounded-lg p-3 cursor-pointer transition-colors duration-state ${
                permission === 'read' ? 'bg-brand-wash' : 'bg-ground hover:bg-inset'
              }`}
            >
              <RadioGroupItem value="read" id="perm-read" className="mt-0.5" data-testid="invite-perm-read" />
              <div className="space-y-0.5">
                <p className="font-medium text-ink text-[15px]">Read only</p>
                <p className="text-sm text-ink-55">
                  They can open and read your records but cannot change anything.
                </p>
              </div>
            </label>
            <label
              className={`flex items-start gap-3 rounded-lg p-3 cursor-pointer transition-colors duration-state ${
                permission === 'read_write' ? 'bg-brand-wash' : 'bg-ground hover:bg-inset'
              }`}
            >
              <RadioGroupItem value="read_write" id="perm-rw" className="mt-0.5" data-testid="invite-perm-rw" />
              <div className="space-y-0.5">
                <p className="font-medium text-ink text-[15px]">Read and write</p>
                <p className="text-sm text-ink-55">
                  Full access. They can upload documents, add organisations and send requests for you.
                </p>
              </div>
            </label>
          </RadioGroup>

          {permission === 'read_write' && <PermissionWarning group={group} />}
          {group === 'health' && <HealthDisclosureNote />}
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onBack}
                data-testid="invite-details-back">
          Back
        </Button>
        <Button onClick={onContinue} disabled={!canProceed}
                data-testid="invite-details-continue">
          Continue
        </Button>
      </DialogFooter>
    </>
  );
};

const PermissionWarning = ({ group }) => {
  // Tone adapts by invitee type.
  let body;
  if (group === 'legal' || group === 'support' || group === 'health') {
    body = (
      "Read and write access lets this person send requests, upload documents "
      + "and write to organisations entirely on your behalf. "
      + "Only grant this to a professional you have engaged to act for you."
    );
  } else if (group === 'personal') {
    body = (
      "Read and write access lets this person send requests, upload documents "
      + "and write to organisations entirely on your behalf. "
      + "This is a big step. Make sure you are comfortable giving them that trust."
    );
  } else {
    body = (
      "Read and write access lets this person send requests, upload documents "
      + "and write to organisations entirely on your behalf. "
      + "Only grant this to someone you completely trust to act in your interests."
    );
  }
  return (
    <p className="rounded-md bg-amber-wash text-amber-ink px-3 py-2 text-sm leading-relaxed"
       data-testid="invite-rw-warning">
      {body}
    </p>
  );
};

const HealthDisclosureNote = () => (
  <p className="rounded-md bg-inset text-ink-70 px-3 py-2 text-sm leading-relaxed">
    Sharing with a treating professional may support your care, but these are
    still deeply personal records. Make sure you are comfortable with
    everything they will be able to see.
  </p>
);


// =========================================================================
// Step 3 — Confirm by typing invitee email back
// =========================================================================
const ConfirmStep = ({ email, confirmEmail, setConfirmEmail, canSubmit, loading, error, onBack, onSubmit }) => (
  <>
    <DialogHeader>
      <DialogTitle className="font-heading text-[22px] font-semibold tracking-[-0.02em]">
        Confirm and sign
      </DialogTitle>
      <DialogDescription className="text-ink-55">
        Type their email address again to confirm. Signing happens on your device.
      </DialogDescription>
    </DialogHeader>
    <div className="space-y-4">
      <p className="p-3 rounded-lg bg-ground text-sm text-ink-70 leading-relaxed">
        When you confirm, your encryption key signs the authorisation document
        and wraps a copy of your key for{' '}
        <span className="font-medium text-ink">{email}</span>. Nothing is sent
        anywhere else.
      </p>

      <div className="space-y-1.5">
        <Label htmlFor="invite-confirm" className="text-[15px] font-semibold">Type their email address again</Label>
        <Input
          id="invite-confirm"
          type="email"
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.target.value)}
          placeholder={email}
          autoComplete="off"
          data-testid="invite-confirm-email"
          disabled={loading}
        />
        {confirmEmail && confirmEmail.trim().toLowerCase() !== email.trim().toLowerCase() && (
          <p className="text-sm text-breach-ink">The two email addresses do not match.</p>
        )}
      </div>

      {error && (
        <p className="text-sm text-breach-ink" data-testid="invite-error">{error}</p>
      )}
    </div>
    <DialogFooter>
      <Button variant="ghost" onClick={onBack} disabled={loading}
              data-testid="invite-confirm-back">
        Back
      </Button>
      <Button
        onClick={onSubmit}
        disabled={!canSubmit || loading}
        data-testid="invite-confirm-submit"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Signing
          </>
        ) : (
          'Grant access'
        )}
      </Button>
    </DialogFooter>
  </>
);


export default InviteDialog;
